export interface MetricWithGrowth {
  value: number;
  growth: number | null;
}

/**
 * Calculate the latest value of a numeric metric and its year-over-year growth.
 *
 * Assumes each record has a `year` field that can be coerced into a number and
 * a numeric field designated by `metricKey`.
 *
 * Returns `null` if the metric doesn't exist or records is empty.
 * Growth will be null if it cannot be calculated (e.g., no previous year or previous value is 0)
 */
export const calcLatestValueAndGrowth = (
  records: Record<string, any>[],
  metricKey: string
): MetricWithGrowth | null => {
  if (!records || records.length === 0) return null;

  // Clone then sort to avoid mutating the original array.
  const sorted = [...records].sort((a, b) => Number(b.year) - Number(a.year));
  const latest = sorted[0];
  const previous = sorted[1];

  if (!latest || typeof latest[metricKey] !== 'number') return null;

  const value = latest[metricKey] as number;
  let growth: number | null = null;

  if (
    previous &&
    typeof previous[metricKey] === 'number' &&
    previous[metricKey] !== 0
  ) {
    growth = ((value - previous[metricKey]) / previous[metricKey]) * 100;
  }

  return { value, growth };
};

export const calculateGrowth = (current: number | undefined, previous: number | undefined): number | null => {
  if (current === undefined || previous === undefined || previous === 0) {
    return null;
  }
  return ((current - previous) / previous) * 100;
};

export function calculateMedianHousingAge(metrics: {
  units_built_1939_or_earlier: number;
  units_built_between_1940_and_1949: number;
  units_built_between_1950_and_1959: number;
  units_built_between_1960_and_1969: number;
  units_built_between_1970_and_1979: number;
  units_built_between_1980_and_1989: number;
  units_built_between_1990_and_1999: number;
  units_built_between_2000_and_2009: number;
  units_built_between_2010_and_2013: number;
  units_built_2014_or_later: number;
  year: number;
}): number {
  // Define time periods with their start and end years for more precise median calculation
  const timePeriods = [
    { 
      name: '1939_or_earlier', 
      count: metrics.units_built_1939_or_earlier, 
      startYear: 1900, // Assume earliest possible year
      endYear: 1939,
      midpoint: 1939
    },
    { 
      name: '1940_1949', 
      count: metrics.units_built_between_1940_and_1949, 
      startYear: 1940, 
      endYear: 1949,
      midpoint: 1945
    },
    { 
      name: '1950_1959', 
      count: metrics.units_built_between_1950_and_1959, 
      startYear: 1950, 
      endYear: 1959,
      midpoint: 1955
    },
    { 
      name: '1960_1969', 
      count: metrics.units_built_between_1960_and_1969, 
      startYear: 1960, 
      endYear: 1969,
      midpoint: 1965
    },
    { 
      name: '1970_1979', 
      count: metrics.units_built_between_1970_and_1979, 
      startYear: 1970, 
      endYear: 1979,
      midpoint: 1975
    },
    { 
      name: '1980_1989', 
      count: metrics.units_built_between_1980_and_1989, 
      startYear: 1980, 
      endYear: 1989,
      midpoint: 1985
    },
    { 
      name: '1990_1999', 
      count: metrics.units_built_between_1990_and_1999, 
      startYear: 1990, 
      endYear: 1999,
      midpoint: 1995
    },
    { 
      name: '2000_2009', 
      count: metrics.units_built_between_2000_and_2009, 
      startYear: 2000, 
      endYear: 2009,
      midpoint: 2005
    },
    { 
      name: '2010_2013', 
      count: metrics.units_built_between_2010_and_2013, 
      startYear: 2010, 
      endYear: 2013,
      midpoint: 2012
    },
    { 
      name: '2014_or_later', 
      count: metrics.units_built_2014_or_later, 
      startYear: 2014, 
      endYear: metrics.year,
      midpoint: metrics.year - 2 // Assume average of recent builds is 2 years before current year
    }
  ];

  // Filter out periods with no units and sort chronologically (oldest first)
  const validPeriods = timePeriods
    .filter(period => period.count > 0)
    .sort((a, b) => a.startYear - b.startYear);

  if (validPeriods.length === 0) return 0;

  const totalUnits = validPeriods.reduce((sum, period) => sum + period.count, 0);
  const medianPosition = totalUnits / 2; // 50th percentile

  // Find the period containing the median
  let cumulativeUnits = 0;
  let medianPeriod: typeof validPeriods[0] | null = null;
  let unitsBeforeMedian = 0;

  for (const period of validPeriods) {
    const previousCumulative = cumulativeUnits;
    cumulativeUnits += period.count;
    
    if (cumulativeUnits >= medianPosition) {
      medianPeriod = period;
      unitsBeforeMedian = previousCumulative;
      break;
    }
  }

  if (!medianPeriod) {
    // Fallback: use the last period if median wasn't found
    medianPeriod = validPeriods[validPeriods.length - 1];
    unitsBeforeMedian = cumulativeUnits - medianPeriod.count;
  }

  // Calculate the exact position within the median period
  const unitsNeededInPeriod = medianPosition - unitsBeforeMedian;
  const proportionInPeriod = unitsNeededInPeriod / medianPeriod.count;

  // Interpolate the exact year within the period
  const medianYear = medianPeriod.startYear + (proportionInPeriod * (medianPeriod.endYear - medianPeriod.startYear));

  // Return the median age (current year minus median build year)
  return Math.round(metrics.year - medianYear);
} 